/**
 * Persist walkthrough progress per user in localStorage.
 * @module features/walkthrough/walkthroughStorage
 */

import { WALKTHROUGH_STORAGE_VERSION } from './walkthroughTypes';

const STORAGE_KEY_PREFIX = 'kl_walkthrough';

/**
 * @param {object|null|undefined} user
 * @returns {string|null}
 */
export function resolveWalkthroughStorageKey(user) {
  if (!user) return null;
  const id = user.id ?? user.email;
  if (id === undefined || id === null || id === '') return null;
  return `${STORAGE_KEY_PREFIX}_${String(id)}`;
}

/**
 * @returns {import('./walkthroughTypes').WalkthroughPersistedState}
 */
export function createDefaultWalkthroughState() {
  return {
    version: WALKTHROUGH_STORAGE_VERSION,
    completedTours: {},
    dismissedAt: null,
  };
}

/**
 * @param {unknown} raw
 * @returns {import('./walkthroughTypes').WalkthroughPersistedState}
 */
export function normalizeWalkthroughState(raw) {
  const defaults = createDefaultWalkthroughState();

  if (!raw || typeof raw !== 'object') {
    return defaults;
  }

  const record = /** @type {Record<string, unknown>} */ (raw);

  if (record.version !== WALKTHROUGH_STORAGE_VERSION) {
    return defaults;
  }

  const completedTours =
    record.completedTours && typeof record.completedTours === 'object'
      ? /** @type {Record<string, number>} */ ({ ...record.completedTours })
      : {};

  const dismissedAt =
    typeof record.dismissedAt === 'number' && Number.isFinite(record.dismissedAt)
      ? record.dismissedAt
      : null;

  return {
    version: WALKTHROUGH_STORAGE_VERSION,
    completedTours,
    dismissedAt,
  };
}

/**
 * @param {string} storageKey
 * @param {Storage|null|undefined} [storage]
 * @returns {import('./walkthroughTypes').WalkthroughPersistedState}
 */
export function loadWalkthroughState(storageKey, storage = getStorage()) {
  if (!storageKey || !storage) {
    return createDefaultWalkthroughState();
  }

  try {
    const raw = storage.getItem(storageKey);
    if (!raw) {
      return createDefaultWalkthroughState();
    }

    return normalizeWalkthroughState(JSON.parse(raw));
  } catch {
    return createDefaultWalkthroughState();
  }
}

/**
 * @param {string} storageKey
 * @param {import('./walkthroughTypes').WalkthroughPersistedState} state
 * @param {Storage|null|undefined} [storage]
 */
export function saveWalkthroughState(storageKey, state, storage = getStorage()) {
  if (!storageKey || !storage) return;

  try {
    storage.setItem(storageKey, JSON.stringify(state));
  } catch {
    // Quota or privacy mode — non-critical.
  }
}

/**
 * @param {import('./walkthroughTypes').WalkthroughPersistedState} state
 * @param {string} tourId
 * @param {number} [tourVersion=1]
 * @returns {import('./walkthroughTypes').WalkthroughPersistedState}
 */
export function markTourCompleted(state, tourId, tourVersion = 1) {
  return {
    ...state,
    completedTours: {
      ...state.completedTours,
      [tourId]: tourVersion,
    },
  };
}

/**
 * @param {import('./walkthroughTypes').WalkthroughPersistedState} state
 * @param {number} [dismissedAt=Date.now()]
 * @returns {import('./walkthroughTypes').WalkthroughPersistedState}
 */
export function markWalkthroughDismissed(state, dismissedAt = Date.now()) {
  return {
    ...state,
    dismissedAt,
  };
}

/**
 * @param {import('./walkthroughTypes').WalkthroughPersistedState} state
 * @param {import('./walkthroughTypes').WalkthroughTourDef} tour
 * @returns {boolean}
 */
export function isTourCompleted(state, tour) {
  const completedVersion = state.completedTours[tour.id];
  if (completedVersion === undefined) return false;
  return completedVersion >= (tour.version ?? 1);
}

function getStorage() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
